// The signed-in contributor's own plays (today and tomorrow, both streams, encores included): the
// "your song plays soon / is playing" notice and the River's "your song" highlight. Refreshed every
// few minutes and whenever a lineup changes (freeze, encore, removal).
import { useEffect } from "react";
import { create } from "zustand";
import { api } from "./api";
import { onLive } from "./live";
import { useSession } from "./session";

export interface MyPlay {
  loopId: string;
  title: string | null;
  bpm: number;
  day: number;
  slot: number;
  repeat: number | null;
  startMs: number;
  endMs: number;
}

interface MyPlaysState {
  loopIds: ReadonlySet<string>;
  plays: MyPlay[];
  /** Server time minus local time when the plays were loaded (ms). */
  offsetMs: number;
}

export const useMyPlays = create<MyPlaysState>(() => ({ loopIds: new Set(), plays: [], offsetMs: 0 }));

const REFRESH_MS = 5 * 60_000;

async function load(): Promise<void> {
  const r = await api<{ now: number; loopIds: string[]; plays: MyPlay[] }>("GET", "/api/me/plays");
  useMyPlays.setState({ loopIds: new Set(r.loopIds), plays: r.plays, offsetMs: r.now - Date.now() });
}

/** Keep the store in step with the session (mount once, in App). */
export function useMyPlaysSync(): void {
  const userId = useSession((s) => s.user?.id ?? null);
  useEffect(() => {
    if (!userId) {
      useMyPlays.setState({ loopIds: new Set(), plays: [] });
      return;
    }
    const refresh = () => void load().catch(() => undefined);
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    const off = onLive((m) => {
      if (m.type === "patch" || m.type === "frozen" || m.type === "hello" || m.type === "day") refresh();
    });
    return () => {
      clearInterval(id);
      off();
    };
  }, [userId]);
}

/** Is `loopId` one of the signed-in user's songs? */
export const useIsMine = (loopId: string | undefined) => useMyPlays((s) => !!loopId && s.loopIds.has(loopId));
